import * as clone from 'clone'
import { StepType } from '@pushly/models/lib/enums/journeys'
import type { IJourneyStep, ITriggerStep } from '@pushly/models/lib/structs/journeys'
import { generateShortID } from './uid'
import { TriggerType, CatalogItemType } from '@pushly/models/lib/enums'
import type { Journey } from '../features/journeys/types/journey-api-response'

export const extractJourneyTrigger = (journey: Journey): ITriggerStep | undefined => {
    const steps = journey.revision?.steps
    if (!steps) return undefined
    return steps.find((s): s is ITriggerStep => s.type === StepType.TRIGGER)
}

export function isTriggerTypeOf(trigger: TriggerType, types: TriggerType | TriggerType[]): boolean {
    types = Array.isArray(types) ? types : [types]
    return types.includes(trigger)
}

export function reassignStepIds(steps: IJourneyStep[]): IJourneyStep[] {
    const reassigned = clone(steps)
    const idMap: Record<string, string> = {}

    // First pass: assign new IDs
    reassigned.forEach((s) => {
        const newId = generateShortID()
        idMap[s.id] = newId
        s.id = newId
    })

    const remap = (id: any) => (id != null ? (idMap[id] ?? id) : id)

    // Second pass: rewrite all step ID references
    reassigned.forEach((s) => {
        const cfg = s.configuration
        if (!cfg) return

        if (cfg.out_step_id != null) cfg.out_step_id = remap(cfg.out_step_id)
        if (cfg.in_step_id != null) cfg.in_step_id = remap(cfg.in_step_id)
        if (cfg.join_step_id != null) cfg.join_step_id = remap(cfg.join_step_id)
        if (cfg.removed_out_step_id != null) cfg.removed_out_step_id = remap(cfg.removed_out_step_id)

        if (Array.isArray(cfg.branches)) {
            cfg.branches = cfg.branches.map((branch: any) => ({
                ...branch,
                out_step_id: remap(branch.out_step_id),
            }))
        }
    })

    return reassigned
}

export const getJourneyTriggerItemType = (journey: Journey): CatalogItemType | undefined => {
    return extractJourneyTrigger(journey)?.configuration?.params?.item_type
}

/**
 * The trigger's item type read directly from a list of steps (e.g. the in-progress nodes being saved),
 * rather than a persisted `Journey`. Use this at save time, where the current trigger lives in the
 * working steps and the `Journey` object may be stale.
 */
export const getStepsTriggerItemType = (steps: IJourneyStep[] | undefined): CatalogItemType | undefined =>
    steps?.find((s): s is ITriggerStep => s.type === StepType.TRIGGER)?.configuration?.params?.item_type

/**
 * The item type to display across journey UI (node labels, the add-step popover, the notification
 * editor's content). Trigger-first — the journey's content macros resolve against the trigger's items
 * (see the "Prefer item type set on journey trigger" note in trigger-section.tsx) — falling back to the
 * domain's catalog type and finally PRODUCT. Centralized so these surfaces don't drift apart.
 */
export const getJourneyDisplayItemType = (
    journey: Journey | undefined,
    domainItemType?: CatalogItemType,
): CatalogItemType =>
    (journey ? getJourneyTriggerItemType(journey) : undefined) ?? domainItemType ?? CatalogItemType.PRODUCT
